import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wallet, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(errDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FBFBF9] bg-grain flex items-center justify-center px-4"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-10 justify-center">
          <div className="w-10 h-10 rounded-md bg-[#1F4E3D] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-display text-2xl font-medium tracking-tight">Kosh</span>
        </div>

        <div className="bg-white border border-[#EAE8E3] rounded-lg p-8 sm:p-10">
          <div className="label-overline mb-2">Account recovery</div>
          <h1 className="font-display text-3xl tracking-tight font-light mb-1">
            Forgot password
          </h1>
          <p className="text-sm text-[#696E69] mb-8">
            Enter your email. Your admin will share a reset link with you privately.
          </p>

          {sent ? (
            <div
              data-testid="forgot-sent"
              className="bg-[#5C8065]/8 border border-[#5C8065]/20 rounded-md p-4 text-sm text-[#1F4E3D]"
            >
              If that email exists, your admin has been notified. They'll send you a reset link soon.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Email</Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  className="mt-1.5"
                />
              </div>
              {error && (
                <div
                  data-testid="forgot-error"
                  className="text-sm text-[#D26A59] bg-[#D26A59]/8 border border-[#D26A59]/20 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full bg-[#1F4E3D] hover:bg-[#163B2E] text-white rounded-lg py-2.5 h-auto mt-2"
              >
                {busy ? "Submitting…" : "Request reset link"}
              </Button>
            </form>
          )}

          <Link
            to="/login"
            data-testid="back-to-login"
            className="mt-6 inline-flex items-center gap-1.5 text-xs text-[#696E69] hover:text-[#212421]"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
